/**
 * Runtime Configuration Helper
 * 
 * This module provides a centralized way to access runtime configuration.
 * It falls back to build-time environment variables for development mode.
 */

declare global {
  interface Window {
    RUNTIME_CONFIG?: {
      REACT_APP_CONFIG_API_HOST?: string;
      REACT_APP_ENGINE_API_HOST?: string;
      REACT_APP_ENGINE_SOCKET_HOST?: string;
      REACT_APP_PAGER_DUTY_HOST?: string;
      REACT_APP_GRAFANA_BASE_URL?: string;
      REACT_APP_HOST?: string;
      REACT_APP_HOST_BASE_PATH?: string;
      REACT_APP_DEFAULT_PAGE?: string;
      REACT_APP_DEFAULT_PAGE_SIZE?: string;
      REACT_APP_DEBOUNCE_DELAY?: string;
      REACT_APP_RETRY_DELAY?: string;
      REACT_APP_TIMEOUT_DURATION?: string;
      REACT_APP_SESSION_WARNING_THRESHOLD?: string;
      REACT_APP_TIMELINE_UPDATE_INTERVAL?: string;
      REACT_APP_RESEARCH_QUERY_MAXIMUM_SUB_QUERY?: string;
      REACT_APP_DATA_GRID_PRO?: string;
    };
  }
}

/**
 * Get a configuration value from runtime config or build-time environment variable
 * @param key - The configuration key to retrieve
 * @param defaultValue - Optional default value if key is not found
 */
export const getConfig = (key: string, defaultValue: string = ''): string => {
  // First check runtime config (injected at container start)
  if (window.RUNTIME_CONFIG && window.RUNTIME_CONFIG[key as keyof typeof window.RUNTIME_CONFIG]) {
    const value = window.RUNTIME_CONFIG[key as keyof typeof window.RUNTIME_CONFIG];
    // Check if value is still a template placeholder (not replaced)
    if (value && !value.startsWith('${')) {
      return value;
    }
  }
  
  // Fall back to build-time environment variable (for development)
  if (process.env[key]) {
    return process.env[key] as string;
  }
  
  return defaultValue;
};

/**
 * Configuration object with all runtime values
 */
export const RuntimeConfig = {
  // API Endpoints
  CONFIG_API_HOST: getConfig('REACT_APP_CONFIG_API_HOST', 'http://localhost:4000'),
  ENGINE_API_HOST: getConfig('REACT_APP_ENGINE_API_HOST', 'http://localhost:5000'),
  ENGINE_SOCKET_HOST: getConfig('REACT_APP_ENGINE_SOCKET_HOST', 'http://localhost:5000'),
  PAGER_DUTY_HOST: getConfig('REACT_APP_PAGER_DUTY_HOST', ''),
  GRAFANA_BASE_URL: getConfig('REACT_APP_GRAFANA_BASE_URL', ''),
  
  // Application Settings
  HOST: getConfig('REACT_APP_HOST', 'http://localhost:3000'),
  HOST_BASE_PATH: getConfig('REACT_APP_HOST_BASE_PATH', '/'),
  
  // Feature Flags & Settings
  DEFAULT_PAGE: getConfig('REACT_APP_DEFAULT_PAGE', '0'),
  DEFAULT_PAGE_SIZE: getConfig('REACT_APP_DEFAULT_PAGE_SIZE', '10'),
  DEBOUNCE_DELAY: getConfig('REACT_APP_DEBOUNCE_DELAY', '400'),
  RETRY_DELAY: getConfig('REACT_APP_RETRY_DELAY', '4000'),
  TIMEOUT_DURATION: getConfig('REACT_APP_TIMEOUT_DURATION', '900000'),
  SESSION_WARNING_THRESHOLD: getConfig('REACT_APP_SESSION_WARNING_THRESHOLD', '1'),
  TIMELINE_UPDATE_INTERVAL: getConfig('REACT_APP_TIMELINE_UPDATE_INTERVAL', '60000'),
  RESEARCH_QUERY_MAXIMUM_SUB_QUERY: getConfig('REACT_APP_RESEARCH_QUERY_MAXIMUM_SUB_QUERY', '5'),
  
  // License Keys
  DATA_GRID_PRO: getConfig('REACT_APP_DATA_GRID_PRO', ''),
};

export default RuntimeConfig;
